import { COMPONENT_QUERY } from '../../../constants/api';
import { FormatLongNumberWithCurrency, toTitleCase } from '../../../constants/utils';
import {
  LEGENDS_HIGHLIGHT_TEXT,
  MONTHLY_OPPS_SEASONALITY,
  MONTHLY_SPENDS_SEASONALITY,
  OPPS_DATA,
  SPEND_AND_OPPS_FILE_NAME_LOCAL_SPEND_MONTHLY,
  SPEND_AND_OPPS_FILE_NAME_LOCAL_SPEND_WEEKLY,
  SPEND_AND_OPPS_FILE_NAME_MONTHLY,
  SPEND_AND_OPPS_FILE_NAME_WEEKLY,
  WEEKLY_OPPS_SEASONALITY,
  WEEKLY_SPENDS_SEASONALITY,
  fiscalMonths,
} from '../constants';
import api from '../../../services/api';
import { formatNumberUsingUnit } from '../../../utils/formatUtils';
import { Typography } from '@mui/material';

const typeOfTable = {
  opps_data: ['actual_opps', 'planned_opps'],
  spends_data: ['actual_spend', 'planned_spend'],
};
export async function getSpendsAndOppsData(payload, key, dataType) {
  let url = COMPONENT_QUERY + '?component=' + key;
  if (dataType) {
    url += '&dataType=' + dataType;
  }
  const response = await api.post(url, payload);
  return response.data;
}

export function transformHeaders(data, store) {
  // Access the results array directly from your data structure.
  const dataHeaders = data.result;
  let headerNameArr = [];
  const filteredData = dataHeaders?.filter((data) => data.store_id === store);

  if (!filteredData.length) return [];

  const uniqueHeaders = new Set();

  filteredData
    .sort((a, b) => a.fiscal_month - b.fiscal_month || a.fiscal_week - b.fiscal_week)
    .forEach((eachHeader) => {
      if (eachHeader.fiscal_month !== null && eachHeader.fiscal_month !== undefined) {
        const headerValue = `${fiscalMonths[eachHeader.fiscal_month]} W${eachHeader.fiscal_week}`;
        const accessorValue = `${fiscalMonths[eachHeader.fiscal_month]}_W${eachHeader.fiscal_week}`;

        // Check if the header is already added to avoid duplicates.
        if (!uniqueHeaders.has(headerValue)) {
          headerNameArr.push({
            Header: headerValue,
            accessor: accessorValue,
          });
          uniqueHeaders.add(headerValue);
        }
      }
    });

  return headerNameArr;
}

// Helper function to check if data should be skipped for weekly transform
function shouldSkipWeeklyData(isCurrentYear, isPlannedData, isDownloadData) {
  return !isCurrentYear && isPlannedData && !isDownloadData;
}

// Helper function to format value based on type and download flag
function formatValue(entry, key, type, isDownloadData) {
  if (type === OPPS_DATA) {
    return entry[key];
  } else if (isDownloadData) {
    return formatNumberUsingUnit(entry[key], '$');
  } else {
    return FormatLongNumberWithCurrency(entry[key]);
  }
}

// Helper function to process single weekly entry
function processWeeklyEntry(
  entry,
  temp_key,
  currentYear,
  type,
  isDownloadData,
  seasonalityKey,
  segregatedData
) {
  for (const key of temp_key) {
    const isCurrentYear = entry.fiscal_year === currentYear;
    const isPlannedData = key.includes('planned');

    if (shouldSkipWeeklyData(isCurrentYear, isPlannedData, isDownloadData)) {
      continue;
    }

    const data_key_prefix = `${entry.fiscal_year}_${key}`;
    const data_key = `${data_key_prefix}`;

    if (fiscalMonths[entry.fiscal_month]) {
      if (!segregatedData[data_key]) {
        segregatedData[data_key] = {
          [seasonalityKey]: toTitleCase(data_key.split('_').join(' ')),
        };
      }
      if (segregatedData[data_key]) {
        const value = formatValue(entry, key, type, isDownloadData);
        segregatedData[data_key][`${fiscalMonths[entry.fiscal_month]}_W${entry.fiscal_week}`] =
          entry[key] !== null ? value : 'N/A';
      }
    }
  }
}

// Helper function to reorder entries with current year planned data first
function reorderEntriesWithPlanned(segregatedData, currentYear, type, seasonalityKey) {
  const entries = Object.entries(segregatedData);
  const plannedLabel = `${currentYear} Planned ${type === OPPS_DATA ? 'Opps' : 'Spend'}`;
  const index = entries?.findIndex(([_key, value]) => value?.[seasonalityKey] === plannedLabel);

  if (entries && index !== -1) {
    const [movedEntry] = entries.splice(index, 1);
    entries.unshift(movedEntry);
  }

  return Object.fromEntries(entries);
}

export function transformWeeklyData(data, type, store, currentYear, isDownloadData = false) {
  const segregatedData = {};
  const temp_key = typeOfTable[type];
  const filteredData = data?.result.filter((data) => data.store_id === store);

  if (!filteredData.length) return {};
  const sortArr = filteredData
    .slice()
    .sort((a, b) => b.fiscal_year - a.fiscal_year || a.fiscal_week - b.fiscal_week);

  const seasonalityKey = type === OPPS_DATA ? WEEKLY_OPPS_SEASONALITY : WEEKLY_SPENDS_SEASONALITY;

  for (const entry of sortArr) {
    if (entry.fiscal_year <= currentYear) {
      processWeeklyEntry(
        entry,
        temp_key,
        currentYear,
        type,
        isDownloadData,
        seasonalityKey,
        segregatedData
      );
    }
  }

  return reorderEntriesWithPlanned(segregatedData, currentYear, type, seasonalityKey);
}

// Helper function to check if monthly data should be included
function shouldIncludeMonthlyData(data_key, currentYear, type, isDownloadData) {
  const includesCurrentActual = data_key.includes(`${currentYear}_actual`);
  const includesCurrentPlanned = data_key.includes(`${currentYear}_planned`);
  const plannedKey = type === OPPS_DATA ? 'planned_opps' : 'planned_spend';
  const isPastActual = !data_key.includes(`${currentYear}`) && !data_key.includes(plannedKey);

  return includesCurrentActual || includesCurrentPlanned || isPastActual || isDownloadData;
}

// Helper function to process single monthly entry
function processMonthlyEntry(options) {
  const {
    entry,
    key,
    data_key,
    type,
    isDownloadData,
    seasonalityKey,
    segregatedData,
    tableHeadersSet,
    currentYear,
  } = options;

  if (fiscalMonths[entry.fiscal_month]) {
    if (!segregatedData[data_key]) {
      if (shouldIncludeMonthlyData(data_key, currentYear, type, isDownloadData)) {
        segregatedData[data_key] = {
          [seasonalityKey]: toTitleCase(data_key.split('_').join(' ')),
        };
      }
    }
    if (segregatedData[data_key]) {
      const value = formatValue(entry, key, type, isDownloadData);
      segregatedData[data_key][`${fiscalMonths[entry.fiscal_month]}`] =
        entry[key] !== null ? value : 'N/A';
    }
    tableHeadersSet.add(`${fiscalMonths[entry.fiscal_month]}`);
  }
}

// Helper function to fill missing headers with N/A
function fillMissingHeaders(segregatedData, tableHeaders) {
  const rowData = [];
  for (const key in segregatedData) {
    const entry = segregatedData[key];
    for (const header of tableHeaders) {
      const accessor = header.accessor;
      if (entry[accessor] === 0) {
        entry[accessor] = 0;
      } else if (!entry[accessor]) {
        entry[accessor] = 'N/A';
      }
    }
    rowData.push(segregatedData[key]);
  }
  return rowData;
}

// Helper function to sort and reorder monthly row data
function sortAndReorderMonthlyData(rowData, currentYear, type, seasonalityKey) {
  rowData?.sort(
    (a, b) => b?.[seasonalityKey]?.split(' ')?.[0] - a?.[seasonalityKey]?.split(' ')?.[0]
  );
  const plannedLabel = `${currentYear} Planned ${type === OPPS_DATA ? 'Opps' : 'Spend'}`;
  const index = rowData?.findIndex((item) => item?.[seasonalityKey] === plannedLabel);

  if (index !== -1) {
    const [movedItem] = rowData.splice(index, 1);
    rowData.unshift(movedItem);
  }
  return rowData;
}

export const transformMonthlyData = (data, type, store, currentYear, isDownloadData = false) => {
  const segregatedData = {};
  const tableHeadersSet = new Set();
  const temp_key = typeOfTable[type];
  const filteredData = data?.result.filter((data) => data.store_id === store);
  if (!filteredData.length) return { formattedRowData: [], tableHeaders: [] };
  const sortArr = filteredData.slice().sort((a, b) => a.fiscal_month - b.fiscal_month);

  const seasonalityKey = type === OPPS_DATA ? MONTHLY_OPPS_SEASONALITY : MONTHLY_SPENDS_SEASONALITY;

  for (const entry of sortArr) {
    for (const key of temp_key) {
      const data_key = `${entry.fiscal_year}_${key}`;
      processMonthlyEntry({
        entry,
        key,
        data_key,
        type,
        isDownloadData,
        seasonalityKey,
        segregatedData,
        tableHeadersSet,
        currentYear,
      });
    }
  }

  const tableHeaders = [
    {
      Header: () => (
        <span style={{ width: '200px', display: 'inline-block' }}>
          {' '}
          {type === OPPS_DATA ? 'Monthly Opps Seasonality' : 'Monthly Spends Seasonality'}
        </span>
      ),
      accessor: seasonalityKey,
    },
    ...Array.from(tableHeadersSet).map((header) => ({
      Header: header.split('_').join(' '),
      accessor: header,
    })),
  ];

  const rowData = fillMissingHeaders(segregatedData, tableHeaders);
  const sortedRowData = sortAndReorderMonthlyData(rowData, currentYear, type, seasonalityKey);

  return { rowData: sortedRowData, tableHeaders };
};

export const getOTPayload = (key, selectedStore, selectedYears, channels = null) => {
  const filters = [
    { col: 'fiscal_year', op: 'in', val: selectedYears },
    { col: 'store_id', op: 'in', val: selectedStore },
  ];

  // Add channel filter if channels are provided
  if (channels && Array.isArray(channels)) {
    filters.push({ col: 'channel', op: 'IN', val: channels });
  }

  const payload = { [key]: { filters: filters } };
  return payload;
};

export const legendHighlightText = () => {
  return (
    <Typography variant="body2" sx={{ textAlign: 'center', padding: 2 }}>
      {LEGENDS_HIGHLIGHT_TEXT}
    </Typography>
  );
};

export const filterData = ({ rowData, currentYear, actualKey, plannedKey, dataKey }) =>
  rowData?.filter((item) => {
    // Check if the item corresponds to "Actual Spend"
    if (item[dataKey].includes(actualKey)) {
      return true; // Always include actual spend data
    }

    // Check if the item corresponds to "Planned Spend" for the current year
    if (item[dataKey].includes(plannedKey)) {
      const yearInLabel = item[dataKey].split(' ')[0]; // Extract year from the label
      return yearInLabel === `${currentYear}`; // Only include planned spend for the current year
    }

    return false; // Exclude everything else
  });

export const getSpendAndOppsFileName = ({ spendToggleState, viewType }) => {
  const isLocal = spendToggleState;
  const isMonthly = viewType === 'Monthly';

  if (isLocal && isMonthly) return SPEND_AND_OPPS_FILE_NAME_LOCAL_SPEND_MONTHLY;
  if (isLocal && !isMonthly) return SPEND_AND_OPPS_FILE_NAME_LOCAL_SPEND_WEEKLY;
  if (!isLocal && isMonthly) return SPEND_AND_OPPS_FILE_NAME_MONTHLY;

  return SPEND_AND_OPPS_FILE_NAME_WEEKLY;
};
